/**
 * Per-key task serialization.
 *
 * Exists for one specific hazard: a read-modify-write against the op log where
 * the "read" is the current chain head.
 *
 *     const baseVersion = await replica.getHead(subject, predicate);
 *     syncClient.submit({ ..., base_version: baseVersion });
 *
 * If a second write to that chain reads the head before the first has been
 * appended locally, both carry the same `base_version`, the server arbitrates
 * the second as stale, and that write is dropped. Serializing per key makes
 * each write observe its predecessor's head, so the ops form a proper causal
 * chain instead of a fork. Per KEY and not globally: writes to unrelated chains
 * are independent, and one stalled write must not hold the others up.
 *
 * Found twice, and worth recording both because the second is what made this a
 * general mechanism rather than a local patch:
 *
 *  1. Composite settings. Every field of `gm_pref_profile` commits the WHOLE
 *     object under one `config.set` key, so filling two fields in quick
 *     succession fires two writes to one chain (e2e `30-gm-pref-profile` 30.1:
 *     `tone` persisted, `content_boundaries` did not, with no error surfaced —
 *     `config.set` opts out of draft-with-conflict; ADR-0004 §5).
 *  2. Ordinary fact editing. Renaming an entity right after creating it lost
 *     the rename ~150 ms later, and setting a validity bound in that window
 *     lost the bound. Every op-log append in `app.ts` now goes through
 *     `appendToChain`, keyed by `opChainKey` below.
 *
 * Deliberately not a general-purpose mutex: no timeouts, no cancellation, no
 * reentrancy. Entries are dropped as soon as a key drains, so the map does not
 * accumulate dead keys over a long session.
 */

/**
 * The serialization key for ONE op-log chain.
 *
 * Every writer to a `(subject, predicate)` chain must take this same key, or
 * they do not interlock. That is not hypothetical: `submitFact` writes fact
 * chains, but so do `submitFactStatus` (ratify / shelve) and the three LLM
 * draft paths, and `entity.create` shares `__entity` with attach-trait,
 * soft-delete and restore. Serializing a function against only itself leaves
 * every cross-function pair on that chain racing exactly as before.
 *
 * NUL-separated rather than joined on a printable character: subjects and
 * predicates are usually minted ids (`location_…`, `pred_…`), but predicates
 * also cover free-form homebrew names and synthetic slots such as
 * `reveal:<predicate>:<viewer>` — so a printable separator would be a collision
 * the type system could never catch.
 */
export function opChainKey(subject: string, predicate: string): string {
  return `${subject}\u0000${predicate}`;
}

/**
 * Tail of each key's chain — always a rejection-normalised promise, so a failed
 * task can never reject the gate a successor is waiting on. Present only while
 * that key has work in flight.
 */
const tails = new Map<string, Promise<void>>();

function ignore(): void {
  /* intentionally empty — a predecessor's failure belongs to ITS caller */
}

/**
 * Run `task` once every previously-queued task for `key` has settled, and
 * resolve with its result.
 *
 * A rejecting task propagates to its own caller but does not poison the chain:
 * the next task for the same key still runs. Without that, one failed write
 * would wedge every later write to that key for the life of the page.
 */
export async function sequenceByKey<T>(key: string, task: () => Promise<T>): Promise<T> {
  const gate = tails.get(key) ?? Promise.resolve();
  const run = gate.then(task);
  const settled = run.then(ignore, ignore);
  tails.set(key, settled);
  try {
    return await run;
  } finally {
    // Clear the key only if nothing was queued behind us — otherwise we would
    // drop a successor's gate and reopen the race we exist to close.
    if (tails.get(key) === settled) tails.delete(key);
  }
}
